<template>
  <q-table
    :columns="columns"
    :rows="postSearches"
    :loading="isLoading"
    v-model:pagination="pagination"
    wrap-cells
    @request="pagination = $event.pagination"
  >
    <template v-slot:body-cell-keywords="props">
      <q-td :props="props">
        {{ $filters.keywords(props.row.keywords) }}
      </q-td>
    </template>
    <template v-slot:body-cell-matches="props">
      <q-td :props="props">
        <view-search-btn :post-search="props.row" @update:postSearch="set" />
      </q-td>
    </template>
    <template v-slot:body-cell-creator="props">
      <q-td :props="props">
        <q-icon name="fa fa-user" />
        <span v-if="props.value">
          {{ props.value.firstName }} {{ props.value.lastName }}
        </span>
        <span v-else>
          N/A
        </span>
      </q-td>
    </template>
    <template v-slot:body-cell-createdAt="props">
      <q-td :props="props">
        <humanize-time :time="props.value" />
      </q-td>
    </template>
    <template v-slot:body-cell-actions="props">
      <q-td :props="props">
        <edit-search-btn
          :post-search-id="props.row.id"
          class="on-left"
          round
          flat
          @edited="set"
        />
        <export-to-report-modal-btn
          :post-search-id="props.row.id"
          class="on-left"
        />
        <delete-search-btn :post-search="props.row" round flat @deleted="load" />
      </q-td>
    </template>
    <template v-slot:no-data>
      <message type="info" class="full-width q-my-sm" style="font-size: .9rem">
        No searches found.
      </message>
    </template>
  </q-table>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  PostSearch,
  PostSearchFilters,
  QTablePagination,
  QueryResult,
} from '@/type';
import HumanizeTime from '@/components/HumanizeTime.vue';
import Message from '@/components/message/Message.vue';
import { HasTaskMixin } from '@/mixins';
import repository from './post-search-repository';
import DeleteSearchBtn from './DeleteSearchBtn.vue';
import ViewSearchBtn from './ViewSearchBtn.vue';
import EditSearchBtn from './EditSearchBtn.vue';
import ExportToReportModalBtn from './export/ExportToReportModalBtn.vue';

interface Data {
  columns: object[],
  pagination: QTablePagination,
  postSearches: PostSearch[],
  task: Promise<QueryResult<PostSearch>>|null,
}

export default defineComponent({
  mixins: [HasTaskMixin],
  components: {
    Message,
    ExportToReportModalBtn,
    EditSearchBtn,
    ViewSearchBtn,
    HumanizeTime,
    DeleteSearchBtn,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
  },
  data(): Data {
    return {
      columns: [
        {
          name: 'keywords',
          label: 'Keywords',
          field: 'keywords',
          align: 'left',
        },
        {
          name: 'matches',
          label: 'Matches',
          field: 'numPostsMatched',
          align: 'center',
        },
        {
          name: 'creator',
          label: 'Creator',
          field: 'createdBy',
          align: 'left',
        },
        {
          name: 'createdAt',
          label: 'Created',
          field: 'createdAt',
          align: 'left',
        },
        {
          name: 'actions',
          label: '',
          align: 'right',
        },
      ],
      pagination: {
        page: 1,
        rowsPerPage: 5,
        rowsNumber: 0,
      },
      postSearches: [],
      task: null,
    };
  },
  computed: {
    offset(): number {
      return (this.pagination.page - 1) * this.pagination.rowsPerPage;
    },
    filters(): PostSearchFilters {
      return {
        offset: this.offset,
        limit: this.pagination.rowsPerPage,
      };
    },
  },
  watch: {
    clientId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
    filters: 'load',
  },
  methods: {
    load() {
      this.task = repository.findAll(this.clientId, this.filters);
      this.task.then((result: QueryResult<PostSearch>) => {
        this.pagination.rowsNumber = result.total;
        this.postSearches = result.items;
      });
    },
    loadOne(postSearchId: number) {
      repository.find(postSearchId).then((postSearch) => {
        this.set(postSearch);
      });
    },
    prepend(search: PostSearch) {
      this.postSearches.unshift(search);
      this.pagination.rowsNumber += 1;
    },
    set(search: PostSearch) {
      const postSearches = [...this.postSearches];
      const i = postSearches.findIndex(s => s.id === search.id);
      if (i >= 0) {
        postSearches.splice(i, 1, search);
        this.postSearches = postSearches;
      }
    },
  },
  filters: {
    keywords(keywords: string[]): string {
      return keywords.join(', ');
    },
  },
});
</script>
